import { Component, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { IDataStudioPark, IDataStudioSignal, IDataStudioDevice, ISeries, DEVICE_TYPES } from 'app/data-studio-redesign/interfaces/data-studio.interface';
import { DEVICE_TYPES_LIST, SIGNALS_TYPES_LIST, findCommonSignals } from 'app/data-studio-redesign/services/data-analysis-helper';
import { uniqueArrObj } from 'app/shared/helper/common.helper';
import { ReplaySubject } from 'rxjs';
import { skip, takeUntil } from 'rxjs/operators';
import { cloneDeep } from 'lodash';
import { ISeriesElement } from '../element-selector/element-selector.component';

@Component({
    selector: 'app-new-series',
    templateUrl: './new-series.component.html',
    styleUrls: ['./new-series.component.scss'],
    standalone: false
})

export class NewSeriesComponent implements OnInit, OnChanges, OnDestroy {

  @Input() parks: IDataStudioPark[];
  @Input() lastIndex: number;
  @Input() ids: string[];
  @Output() closeNew = new EventEmitter();
  @Output() createNew = new EventEmitter();

  public parkElements: ISeriesElement[];
  public deviceElements: ISeriesElement[];
  public signalElements: ISeriesElement[];
  
  public devicesList: Partial<IDataStudioDevice>[] = [];
  public signalsList: IDataStudioSignal[] = [];
  public series: ISeries[];
  public filteredSignals: IDataStudioSignal[];
  public filteredDevices: Partial<ISeriesElement>[];
  public loadingDeviceData: boolean = false;
  public expanded: boolean;
  public onlyCommon: boolean;

  public typesList = cloneDeep([...SIGNALS_TYPES_LIST]);
  public filteredTypesList = [];
  public deviceTypeList = cloneDeep([...DEVICE_TYPES_LIST]);

  public readonly MAX_SERIES_TO_ADD: number = 50;

  private unsubscriber = new ReplaySubject();

  constructor(private translateService: TranslateService) {}


  public ngOnInit(): void {
    this.expanded = false;
    this.onlyCommon = false;
    this.typesList.forEach(d => d.titleTranslationKey = this.translateService.instant(d.titleTranslationKey));
    this.subscribeToLangChange();
  }
  
  public ngOnChanges(changes: SimpleChanges): void {
    if (changes?.parks?.currentValue) {
      this.parks = changes.parks.currentValue.map(park => ({
        ...park,
        devices: park.devices.map(device => ({...device, selected: false})),
        selected: false
      }));
      this.parks.sort((a, b) => {
        return a.fe_name.localeCompare(b.fe_name);
      });
      this.setParkElements();
    }
  }

  public ngOnDestroy(): void {
        this.unsubscriber.next(true);

    this.unsubscriber.complete();
  }

  public setParkElements() {
    this.parkElements = this.parks.map(p => ({
      id: p._id,
      name: p.fe_name,
      deviceType: DEVICE_TYPES.PARK,
      selected: false
    }));
  }

  public setTurbineElements() {
    this.deviceElements = this.devicesList.map(p => ({
      id: p._id,
      name: p.code,
      deviceType: p.deviceType,
      selected: false
    }));
    this.filteredDevices = [...this.deviceElements];
  }

  public setSignalElements() {
    this.signalElements = this.filteredSignals.map(p => ({
      id: p.displayName,
      name: p.displayName,
      deviceType: p.deviceType,
      selected: p.selected
    }));
  }

  public onParkSelect(item) {
    const park = this.parks.find(p => item.id === p._id);
    const devicesList = park.devices;
    park.devices.forEach(t => t.parkId = park._id);
    park.selected = item.selected;
    if (item.selected) {
      this.devicesList = [...this.devicesList, ...devicesList];
    } else {
      devicesList.forEach(device => {
        device.selected = false;
        return device;
      });
      this.devicesList = this.devicesList.filter(
        item1 => !devicesList.some((item2) => item1.name === item2.name)
      );
    }
    this.toggleParkSelection();
  }

  public async onAllParksSelect(filteredItems) {
    this.loadingDeviceData = true;
    this.devicesList = [];
    const selectedItems = filteredItems.filter(i => i.selected);
    this.parks.forEach(park => {
      const isSelected = !!selectedItems.find(i => i.id === park._id);
      park.selected = isSelected;
      if (isSelected) {
        park.devices.forEach(t => t.parkId = park._id);

        const newItems = park.devices
          .filter(a => a.signalsList.length > 0)
          .map(a => ({ ...a, selected: false }));
        this.devicesList.push(...newItems);
      }
    });
    this.toggleParkSelection();
    this.loadingDeviceData = false;
  }

  public toggleParkSelection() {
    this.devicesList = uniqueArrObj(this.devicesList, '_id')
      .sort((a, b) => a?.code.localeCompare(b?.code, undefined, {numeric: true, sensitivity: 'base'}));
    this.setTurbineElements();
    this.signalsList = [];
    this.signalElements = [];
    this.createSeries();
  }


  public onTurbineSelect(item) {
    const device = this.devicesList.find(p => item.id === p._id);
    device.selected = item.selected;

    this.updateSignalList();
  }

  public onAllTurbinesSelect(filteredItems) {
    const selectedItems = filteredItems.filter(i => i.selected);
    this.devicesList.forEach(t => {
      const isSelected = !!selectedItems.find(i => i.id === t._id);
      t.selected = isSelected;
    });
    
    this.updateSignalList();
  }

  private updateSignalTypesFilter() {
    this.filteredTypesList.forEach(d => {
      const count = this.signalsList.filter(a => a.signalGroupType === d.title && a.selected).length;
      d.titleTranslationKey = this.translateService.instant(d.translationKey) + (count > 0 ? ` (${count} ${this.translateService.instant('DATA_STUDIO.SIGNALS.SELECTED')})` : '');
    })
    this.filterBySignalType();
    this.createSeries();
  }

  public onSignalSelect(item) {
    const signal = this.filteredSignals.find(p => item.id === p.displayName);
    this.toggleSignalSelection({ checked: item.selected }, signal);
  }

  public toggleSignalSelection({checked}, signal) {
    this.signalsList.find(s => s.name === signal.name).selected = checked;
    this.updateSignalTypesFilter();
  }

  public onAllSignalsSelect(filteredItems) {
    const selectedItems = filteredItems.filter(i => i.selected);
    this.signalsList.forEach(s => {
      const isSelected = !!selectedItems.find(i => i.id === s.displayName);
      s.selected = isSelected;
    });
    this.updateSignalList();
  }

  public close() {
    this.cleanSelection();
    this.closeNew.emit();
  }

  public addNewSeries() {
    this.createNew.emit(this.series);
    this.cleanSelection();
  }

  public toggleExpand() {
    this.expanded = !this.expanded;
  }

  public pinToggle() {
    this.onlyCommon = !this.onlyCommon;
    this.filterBySignalType();
  }

  public filterBySignalType() {
    let signals;
    if (this.onlyCommon) {
      const selectedSignalNames = this.signalsList.filter(s => s.selected).map(s => s.name);
      const devices = this.devicesList.filter(device => device.selected);
      signals = uniqueArrObj(findCommonSignals(devices), 'displayName')
        .map(s => ({ ...s, selected: selectedSignalNames.includes(s.name)})); 
    } else {
      signals = this.signalsList;
    }
    this.filteredSignals = signals
      .sort((a, b) => a.displayName.localeCompare(b.displayName, undefined, {numeric: true, sensitivity: 'base'}))
      .filter(signal =>
        this.filteredTypesList.filter(type => type.checked).map(type => type.title).includes(signal.signalGroupType)
      );
    this.setSignalElements();
  }

  public filterByDeviceType() {
    const filteredAssetTypes = this.deviceTypeList.filter(d => d.checked).map(d => d.type);
    this.filteredDevices = this.deviceElements.filter(device => filteredAssetTypes.includes(device.deviceType));
  }

  private createSeries() {
    const selectedDevices = this.devicesList.filter(device => device.selected);
    this.series = [];
    this.signalsList.filter(signal => signal.selected).forEach(({name, displayName, unit}) => {
      selectedDevices.forEach(device => {
        if (device.signalsList?.some(signalEl => signalEl.name === name) && !this.ids?.includes(`${device.code}_${name}`)) {
          const isPark = device.deviceType === DEVICE_TYPES.PARK;
          this.series.push({
            id: `${device.code.replace(/ /g, '_')}_${name}`,
            chartData: [],
            type: 'line',
            queryData: device.queryData,
            chartTypeName: 'Line',
            yAxis: ++this.lastIndex,
            color: `#${Math.floor(Math.random()*16777215).toString(16)}`,
            granularity: '1h',
            parkId: device.parkId,
            parkName: device.name,
            parkDisplayName: device.parkName,
            signal: name,
            signalName: `${displayName}`,
            device: isPark ? undefined : device.code,
            assetId: device._id,
            deviceName: isPark ? undefined : device.name,
            deviceType: device.deviceType,
            unit,
            newItem: true,
            visible: false
          })
        }
      });
    })
  }

  private updateSignalList() {
    const selectedSignalNames = this.signalsList.filter(s => s.selected).map(s => s.name);
    const signalsList = this.devicesList.filter(device => device.selected).map(device => device.signalsList).flat().map(o => Object.assign({}, o));
    this.signalsList = uniqueArrObj(signalsList, 'displayName')
      .sort((a, b) => a.displayName.localeCompare(b.displayName, undefined, {numeric: true, sensitivity: 'base'}))
      .map(s => ({ ...s, selected: selectedSignalNames.includes(s.name)}))

    const uniqueSignalCategories = [...new Set(this.signalsList.map(s => s.signalGroupType))]
    this.filteredTypesList = this.typesList.filter(t => uniqueSignalCategories.includes(t.title));
    this.updateSignalTypesFilter();
  }

  private cleanSelection() {
    this.filteredSignals = [];
    this.signalsList = [];
    this.devicesList = [];
    this.onlyCommon = false;
    this.series = [];
    this.typesList = [...SIGNALS_TYPES_LIST].map(type => {
      type.checked = true;
      return type;
    });
    this.filteredTypesList = this.typesList.map(t => t.checked = true);
    this.expanded = false;
  }

  private subscribeToLangChange(): void {
    this.translateService.onLangChange
      .pipe(skip(1), takeUntil(this.unsubscriber))
      .subscribe(() => this.updateSignalTypesFilter());
  }
}
